import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const AssignKPIs = () => {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [kpis, setKpis] = useState([{ kpi_title: '', kpi_description: '', weightage: '' }]);
  const [loading, setLoading] = useState(false);

  const managerEmpCode = localStorage.getItem('emp_code');

  useEffect(() => {
    fetchCycles();
    fetchTeamMembers();
  }, []);

  const fetchCycles = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/appraisal/active-cycle');
      const data = await response.json();
      setCycles([data]);
      setSelectedCycle(data.cycle_id);
    } catch (error) {
      console.error('Error fetching cycles:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`http://localhost:8000/api/manager/team-members/${managerEmpCode}`);
      const data = await response.json();
      setTeamMembers(data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const addKPI = () => {
    setKpis([...kpis, { kpi_title: '', kpi_description: '', weightage: '' }]);
  };

  const removeKPI = (index) => {
    setKpis(kpis.filter((_, i) => i !== index));
  };

  const updateKPI = (index, field, value) => {
    const updated = [...kpis];
    updated[index][field] = value;
    setKpis(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const totalWeight = kpis.reduce((sum, kpi) => sum + parseFloat(kpi.weightage || 0), 0);
    if (totalWeight !== 100) {
      alert('Total weightage must equal 100%');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        cycle_id: parseInt(selectedCycle),
        kpis: kpis.map(kpi => ({
          emp_code: selectedEmployee,
          kpi_title: kpi.kpi_title,
          kpi_description: kpi.kpi_description,
          weightage: parseFloat(kpi.weightage)
        }))
      };

      const response = await fetch('http://localhost:8000/api/manager/assign-kpis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('KPIs assigned successfully!');
        setKpis([{ kpi_title: '', kpi_description: '', weightage: '' }]);
        setSelectedEmployee('');
      } else {
        alert('Failed to assign KPIs');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Assign KPIs to Team Members</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Appraisal Cycle</label>
              <select
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                required
              >
                <option value="">Select Cycle</option>
                {cycles.map(cycle => (
                  <option key={cycle.cycle_id} value={cycle.cycle_id}>
                    {cycle.year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                required
              >
                <option value="">Select Employee</option>
                {teamMembers.map(emp => (
                  <option key={emp.emp_code} value={emp.emp_code}>
                    {emp.name} ({emp.emp_code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">KPIs</h3>
              <button
                type="button"
                onClick={addKPI}
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5" />
                Add KPI
              </button>
            </div>

            {kpis.map((kpi, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-gray-700">KPI {index + 1}</h4>
                  {kpis.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeKPI(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">KPI Title</label>
                    <input
                      type="text"
                      value={kpi.kpi_title}
                      onChange={(e) => updateKPI(index, 'kpi_title', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., Project Delivery"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Description/Expectation</label>
                    <textarea
                      value={kpi.kpi_description}
                      onChange={(e) => updateKPI(index, 'kpi_description', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows="2"
                      placeholder="Describe the expected performance..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Weightage (%)</label>
                    <input
                      type="number"
                      value={kpi.weightage}
                      onChange={(e) => updateKPI(index, 'weightage', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., 40"
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                Total Weightage: {kpis.reduce((sum, kpi) => sum + parseFloat(kpi.weightage || 0), 0)}% 
                {kpis.reduce((sum, kpi) => sum + parseFloat(kpi.weightage || 0), 0) === 100 && (
                  <CheckCircleIcon className="inline h-5 w-5 text-green-600 ml-2" />
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => {
                setKpis([{ kpi_title: '', kpi_description: '', weightage: '' }]);
                setSelectedEmployee('');
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign KPIs'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignKPIs;
